/**
 * ApprovalInboxPage — 결재 수신함 (내가 처리해야 할 결재 목록).
 *
 * 【역할】
 * - 현재 로그인 사용자의 결재 순번에 도달한 대기(PENDING) 건만 표시
 * - 7-role 승인 체계(APPROVE/FINAL/DECIDE/AGREE/CC/DELEGATE/NOTIFY) 지원
 *
 * 【주요 기능】
 * - 내 차례 결재 목록 조회 (status = PENDING AND assigneeId = me)
 * - 행 클릭 → Modal 로 상세 + 결재선 Timeline 표시
 * - Approve / Reject 액션 (코멘트 입력)
 * - 직렬(SERIAL) / 병렬(PARALLEL) 실행 유형 구분
 *
 * 【사용 훅】
 * - useDataSet (ds)
 * - useTransaction
 * - useLabel (t)
 *
 * 【Backend RPC serviceName】
 * - approval/searchList  — 내 차례 목록 (assignee 필터)
 * - approval/searchById  — 단건 상세 + 결재선
 * - approval/act         — 결재 처리 (approve/reject + comment)
 *
 * 【DataSet 구조】
 * - ds_approval: 결재 목록 (PK: approvalId)
 *
 * 【사용처】 router/index.tsx — `{ path: 'approval/inbox' }`
 * 【포팅 원본】 react-fastapi 신규 화면 (7-role 승인 체계)
 */
import { useEffect, useState } from 'react'
import { Button, Input, Modal, Space, Tag, Typography, message } from 'antd'
import { CheckOutlined, CloseOutlined, ReloadOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useDataSet, useTransaction } from '@/framework'
import type { TrackedRow } from '@/framework/useDataSet'
import { useLabel } from '@/framework/useLabel'
import PageContainer from '@/components/common/PageContainer'
import DataTable from '@/components/common/DataTable'

const { Title, Text } = Typography

interface ApprovalRow {
  approvalId: number
  title: string
  description?: string
  requesterId: string
  status: string
  execType: 'SERIAL' | 'PARALLEL'
  createdAt?: string
}

interface ApprovalStep {
  stepId: number
  approvalId: number
  seq: number
  role: string
  assigneeId: string
  assigneeName?: string
  status: string
  comment?: string
}

const STATUS_COLOR: Record<string, string> = {
  PENDING: 'processing',
  APPROVED: 'success',
  REJECTED: 'error',
  WITHDRAWN: 'default',
  DELEGATED: 'warning'
}

/**
 * ApprovalInboxPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function ApprovalInboxPage() {
  const { t } = useLabel()
  const { transaction } = useTransaction()
  const listDs = useDataSet<ApprovalRow>([])
  const [detail, setDetail] = useState<{ header: ApprovalRow; steps: ApprovalStep[] } | null>(null)
  const [comment, setComment] = useState('')

  const load = () =>
    transaction({
      id: 'load',
      service: 'approval/searchList',
      action: 'search',
      inds: { ds_search: { mode: 'inbox' } },
      outds: { ds_approval: listDs }
    })

  useEffect(() => {
    load()
  }, [])

  const openDetail = async (approvalId: number) => {
    const res = await transaction({
      id: 'detail',
      service: 'approval/searchById',
      action: 'search',
      inds: { ds_search: { approvalId } }
    })
    const header = (res.data?.ds_approval as { rows?: ApprovalRow[] } | undefined)?.rows?.[0]
    const steps = (res.data?.ds_step as { rows?: ApprovalStep[] } | undefined)?.rows ?? []
    if (header) setDetail({ header, steps })
  }

  const act = async (action: 'APPROVE' | 'REJECT') => {
    if (!detail) return
    // 내가 처리해야 할 step 찾기 (PENDING + 내 id)
    const myStep = detail.steps.find(
      (s) => s.status === 'PENDING' && s.assigneeId === 'admin' // TODO: authStore.user.loginId
    )
    if (!myStep) {
      void message.warning(t('MSG_NO_PENDING_STEP', '처리 가능한 단계가 없습니다.'))
      return
    }
    const res = await transaction({
      id: 'act',
      service: 'approval/act',
      action: 'save',
      inds: {
        ds_action: {
          rows: [
            {
              approvalId: detail.header.approvalId,
              stepId: myStep.stepId,
              action,
              comment
            }
          ]
        }
      }
    })
    if (res.success) {
      void message.success(t('MSG_SAVE_SUCCESS', '저장되었습니다.'))
      setDetail(null)
      setComment('')
      load()
    }
  }

  const visibleRows = listDs.getVisibleRows()

  const columns: ColumnsType<TrackedRow<ApprovalRow>> = [
    { title: '#', dataIndex: ['data', 'approvalId'], width: 80 },
    {
      title: t('LBL_TITLE', '제목'),
      dataIndex: ['data', 'title'],
      render: (_v, r) => <a onClick={() => openDetail(r.data.approvalId)}>{r.data.title}</a>
    },
    { title: t('LBL_REQUESTER', '기안자'), dataIndex: ['data', 'requesterId'], width: 120 },
    {
      title: t('LBL_EXEC_TYPE', '진행 방식'),
      dataIndex: ['data', 'execType'],
      width: 100,
      render: (v: string) => <Tag>{v}</Tag>
    },
    {
      title: t('LBL_STATUS', '상태'),
      dataIndex: ['data', 'status'],
      width: 110,
      render: (v: string) => <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag>
    },
    { title: t('LBL_CREATED_AT', '생성일시'), dataIndex: ['data', 'createdAt'], width: 170 }
  ]

  return (
    <PageContainer style={{ overflow: 'hidden' }}>
      <Space style={{ marginBottom: 12, justifyContent: 'space-between', width: '100%' }}>
        <Title level={3} style={{ margin: 0 }}>
          {t('LBL_APPROVAL_INBOX', '결재함')}
        </Title>
        <Button icon={<ReloadOutlined />} onClick={load}>
          {t('BTN_REFRESH', '새로고침')}
        </Button>
      </Space>

      <DataTable<TrackedRow<ApprovalRow>>
        rowKey={(r) => String(r.data.approvalId)}
        columns={columns}
        dataSource={visibleRows}
        size="small"
      />

      <Modal
        open={!!detail}
        onCancel={() => setDetail(null)}
        width={720}
        title={detail?.header.title}
        footer={
          <Space>
            <Button onClick={() => setDetail(null)}>{t('BTN_CANCEL', '취소')}</Button>
            <Button danger icon={<CloseOutlined />} onClick={() => act('REJECT')}>
              {t('BTN_REJECT', '반려')}
            </Button>
            <Button type="primary" icon={<CheckOutlined />} onClick={() => act('APPROVE')}>
              {t('BTN_APPROVE_ACTION', '승인')}
            </Button>
          </Space>
        }
      >
        {detail && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text type="secondary">{t('LBL_REQUESTER', '기안자')}: </Text>
              <Text strong>{detail.header.requesterId}</Text>
              <Tag
                style={{ marginLeft: 12 }}
                color={STATUS_COLOR[detail.header.status] || 'default'}
              >
                {detail.header.status}
              </Tag>
            </div>
            <div>
              <Text>{detail.header.description}</Text>
            </div>
            <Title level={5}>{t('LBL_APPROVAL_LINE', '결재선')}</Title>
            <DataTable
              rowKey="stepId"
              size="small"
              pagination={false}
              dataSource={detail.steps}
              columns={[
                { title: '#', dataIndex: 'seq', width: 50 },
                { title: t('LBL_ROLE', '역할'), dataIndex: 'role', width: 110 },
                {
                  title: t('LBL_ASSIGNEE', '담당자'),
                  dataIndex: 'assigneeName',
                  render: (v, r: ApprovalStep) => v || r.assigneeId
                },
                {
                  title: t('LBL_STATUS', '상태'),
                  dataIndex: 'status',
                  width: 100,
                  render: (v) => <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag>
                },
                { title: t('LBL_COMMENT', '의견'), dataIndex: 'comment' }
              ]}
            />
            <Input.TextArea
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('PH_COMMENT', '의견을 입력하세요')}
            />
          </Space>
        )}
      </Modal>
    </PageContainer>
  )
}
